// src/components/coordinateGrid/PointRenderer.tsx
/**
 * PointRenderer Component
 * 
 * Renders plotted points with various styles and labels.
 * Phase 3: Points and Basic Shapes
 */

import React, { useMemo, useCallback } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { RootState } from '../../types/redux';
import { selectAllPoints, selectPoint } from '../../store/slices/plotPointsSlice';
import { PlottedPoint, Point2D, PointStyle, PointSize, CoordinatePosition } from '../../types/coordinateGrid';

interface PointRendererProps {
  /** Canvas dimensions */
  width: number;
  height: number;
  /** Viewport transform */
  scaleX: number;
  scaleY: number;
  offsetX: number;
  offsetY: number;
  /** Dark mode */
  isDark?: boolean;
}

/**
 * Convert math coordinates to screen coordinates
 */
function mathToScreen(
  point: Point2D,
  scaleX: number,
  scaleY: number,
  offsetX: number,
  offsetY: number
): { x: number; y: number } {
  return {
    x: offsetX + point.x * scaleX,
    y: offsetY - point.y * scaleY, // Y is inverted in screen coordinates
  };
}

/**
 * Check if a point is within the visible viewport
 */
function isPointVisible(
  point: Point2D,
  width: number,
  height: number,
  scaleX: number,
  scaleY: number,
  offsetX: number,
  offsetY: number
): boolean {
  const screen = mathToScreen(point, scaleX, scaleY, offsetX, offsetY);
  return screen.x >= -50 && screen.x <= width + 50 &&
         screen.y >= -50 && screen.y <= height + 50;
}

/**
 * Get size in pixels for point size
 */
function getPointSize(size: PointSize): number {
  switch (size) {
    case 'small':
      return 4;
    case 'medium':
      return 6;
    case 'large':
      return 8;
    default:
      return 6;
  }
}

/**
 * Get coordinate label position
 */
function getLabelPosition(
  point: Point2D,
  position: CoordinatePosition,
  scaleX: number,
  scaleY: number,
  offsetX: number,
  offsetY: number,
  width: number
): { x: number; y: number } {
  const screen = mathToScreen(point, scaleX, scaleY, offsetX, offsetY);
  const offset = 12;

  switch (position) {
    case 'top':
      return { x: screen.x, y: screen.y - offset };
    case 'bottom':
      return { x: screen.x, y: screen.y + offset };
    case 'left':
      return { x: screen.x - offset, y: screen.y };
    case 'right':
      return { x: screen.x + offset, y: screen.y };
    case 'auto':
    default:
      // Auto: prefer top-right, but adjust if near edges
      if (screen.y < 30) {
        return { x: screen.x, y: screen.y + offset }; // Bottom if too close to top
      }
      if (screen.x > width - 100) {
        return { x: screen.x - offset, y: screen.y - offset }; // Left if too close to right
      }
      return { x: screen.x + offset, y: screen.y - offset }; // Default: top-right
  }
}

/**
 * Render a point marker based on style
 */
function renderPointMarker(
  point: PlottedPoint,
  screenX: number,
  screenY: number,
  size: number,
  color: string,
  isSelected: boolean
): React.ReactNode {
  const strokeWidth = isSelected ? 2 : 1;
  const strokeColor = isSelected ? (isSelected ? '#F59E0B' : color) : color;

  switch (point.style) {
    case 'dot':
      return (
        <circle
          cx={screenX}
          cy={screenY}
          r={size}
          fill={color}
          stroke={strokeColor}
          strokeWidth={strokeWidth}
          className="point-marker point-dot"
        />
      );

    case 'cross':
      const crossSize = size * 1.5;
      return (
        <g className="point-marker point-cross">
          <line
            x1={screenX - crossSize}
            y1={screenY - crossSize}
            x2={screenX + crossSize}
            y2={screenY + crossSize}
            stroke={color}
            strokeWidth={strokeWidth * 2}
            strokeLinecap="round"
          />
          <line
            x1={screenX - crossSize}
            y1={screenY + crossSize}
            x2={screenX + crossSize}
            y2={screenY - crossSize}
            stroke={color}
            strokeWidth={strokeWidth * 2}
            strokeLinecap="round"
          />
        </g>
      );

    case 'circle':
      return (
        <circle
          cx={screenX}
          cy={screenY}
          r={size}
          fill="none"
          stroke={color}
          strokeWidth={strokeWidth}
          className="point-marker point-circle"
        />
      );

    case 'square':
      return (
        <rect
          x={screenX - size}
          y={screenY - size}
          width={size * 2}
          height={size * 2}
          fill={color}
          stroke={strokeColor}
          strokeWidth={strokeWidth}
          className="point-marker point-square"
        />
      );

    default:
      return null;
  }
}

export const PointRenderer: React.FC<PointRendererProps> = ({
  width,
  height,
  scaleX,
  scaleY,
  offsetX,
  offsetY,
  isDark = false,
}) => {
  const dispatch = useDispatch();
  const points = useSelector(selectAllPoints);

  // Handle point click
  const handlePointClick = useCallback((id: string, e: React.MouseEvent) => {
    e.stopPropagation();
    dispatch(selectPoint({ id, ctrlKey: e.ctrlKey || e.metaKey }));
  }, [dispatch]);

  // Filter visible points and render
  const pointElements = useMemo(() => {
    return points
      .filter((point) => isPointVisible({ x: point.x, y: point.y }, width, height, scaleX, scaleY, offsetX, offsetY))
      .map((point) => {
        const screen = mathToScreen({ x: point.x, y: point.y }, scaleX, scaleY, offsetX, offsetY);
        const size = getPointSize(point.size);
        const textColor = isDark ? '#E5E7EB' : '#1F2937';

        // Get label position
        const labelPos = getLabelPosition(
          { x: point.x, y: point.y },
          point.coordinatePosition,
          scaleX,
          scaleY,
          offsetX,
          offsetY,
          width
        );

        return (
          <g
            key={point.id}
            className={`point-${point.id}`}
            onClick={(e) => handlePointClick(point.id, e)}
            style={{ cursor: 'pointer' }}
          >
            {/* Point marker */}
            {renderPointMarker(point, screen.x, screen.y, size, point.color, point.isSelected)}

            {/* Label */}
            {point.label && (
              <text
                x={screen.x + size + 4}
                y={screen.y - size - 4}
                fill={textColor}
                fontSize="12"
                fontWeight="500"
                className="point-label"
              >
                {point.label}
              </text>
            )}

            {/* Coordinates */}
            {point.showCoordinates && (
              <text
                x={labelPos.x}
                y={labelPos.y}
                fill={textColor}
                fontSize="10"
                className="point-coordinates"
                textAnchor={point.coordinatePosition === 'left' ? 'end' : 'start'}
              >
                ({point.x.toFixed(1)}, {point.y.toFixed(1)})
              </text>
            )}
          </g>
        );
      });
  }, [points, width, height, scaleX, scaleY, offsetX, offsetY, isDark, handlePointClick]);

  return <g className="points-layer">{pointElements}</g>;
};

